import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Plus, Trash2, Globe } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { Field } from '@vnetwork/ui/forms';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  Input,
  Textarea,
  Checkbox,
  IconButton,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Skeleton,
  MultiSelect,
} from '@vnetwork/ui/primitives';
import { useToast } from '@vnetwork/ui/feedback';
import {
  useServiceBotStrategy,
  useServiceBotEnabledSites,
  useCreateServiceBotStrategy,
  useUpdateServiceBotStrategy,
  emptyBotStrategy,
  ALL_CRAWLER_IDS,
  BOT_CRAWLERS,
  BOT_METRICS,
  BOT_METRIC_OPERATORS,
  BOT_METRIC_VALUES,
  BOT_SILENCE_ACTIONS,
  BOT_WHITELIST_METRICS,
  botWhitelistMetric,
  botWhitelistMetricLabel,
  botWhitelistOpLabel,
  type BotStrategy,
  type BotBrowserInterrogation,
  type BotRequestWhitelistRule,
} from '@/api/waap';
import { EmptyState } from '@vnetwork/ui/data';
import { geoCountries, countryName } from '@/pages/services/waf/_shared/geo';
import { WaapConsoleBreadcrumbs } from '@/pages/services/waap/WaapConsoleBreadcrumbs';
import { CheckboxField } from '@/pages/services/waap/components/CheckboxField';
import { errMessage } from '@/pages/services/waap/waap-util';
import { trackFeatureAction } from '@/lib/analytics-events';
import styles from './BotManagementForm.module.css';

const NAME_MAX = 26;
const DESC_MAX = 200;

const labelOf = (cat: { value: string; label: string }[], v: string) =>
  cat.find((o) => o.value === v)?.label ?? v;

/**
 * Add / Edit a Bot Management rule (vendor "bot_strategy"). Account-level: the
 * rule is applied to one or more sites via "Apply to". Mirrors the SwiftFederation
 * portal form, translated to console conventions. Crawl Control's catalog is
 * hardcoded (see BOT_CRAWLERS). Routes: bot-management/new and bot-management/:id.
 */
export function BotManagementForm() {
  const navigate = useNavigate();
  const { serviceId, id } = useParams<{ serviceId: string; id: string }>();
  const isEdit = !!id;
  const { show } = useToast();
  const base = `/services/waap/${serviceId}`;

  const { data: existing, isLoading: loadingExisting } = useServiceBotStrategy(serviceId, id);
  const { data: sites = [] } = useServiceBotEnabledSites(serviceId);

  const [form, setForm] = useState<BotStrategy>(() => emptyBotStrategy(ALL_CRAWLER_IDS));
  const set = <K extends keyof BotStrategy>(key: K, value: BotStrategy[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  // Seed the form from the loaded strategy in edit mode. The vendor returns null
  // (not []) for empty list fields, so coalesce them to [] — the form maps/filters
  // over these and would crash on null.
  useEffect(() => {
    if (existing)
      setForm({
        ...existing,
        crawl_control: existing.crawl_control ?? [],
        browser_interrogation_list: existing.browser_interrogation_list ?? [],
        request_whitelist: existing.request_whitelist ?? [],
        sites: existing.sites ?? [],
      });
  }, [existing]);

  // Browser-interrogation draft row.
  const [bi, setBi] = useState({
    matchParameter: BOT_METRICS[0].value,
    matchOperator: BOT_METRIC_OPERATORS[0].value,
    matchValue: BOT_METRIC_VALUES[0].value,
  });
  const addInterrogation = () => {
    const entry: BotBrowserInterrogation = { enable: 1, ...bi };
    set('browser_interrogation_list', [...form.browser_interrogation_list, entry]);
  };
  const removeInterrogation = (i: number) =>
    set(
      'browser_interrogation_list',
      form.browser_interrogation_list.filter((_, idx) => idx !== i),
    );

  // Request-whitelist draft row. `value` holds text metrics (path/ua); `values`
  // holds list metrics (extension/country, serialised as an array).
  const [wl, setWl] = useState({
    key: BOT_WHITELIST_METRICS[0].key,
    operation: BOT_WHITELIST_METRICS[0].operations[0].value,
    value: '',
    values: [] as string[],
  });
  const wlMetric = botWhitelistMetric(wl.key) ?? BOT_WHITELIST_METRICS[0];
  // ISO country options for the "Country ISO Code" metric (still creatable, so an
  // unlisted code can be typed).
  const countryOptions = useMemo(
    () => geoCountries.map((c) => ({ value: c.code, label: `${countryName(c.code)} (${c.code})` })),
    [],
  );
  // Changing the metric resets the operation (each metric has its own set) and
  // clears the value (text vs list entry differ).
  const changeWlMetric = (key: string) => {
    const m = botWhitelistMetric(key) ?? BOT_WHITELIST_METRICS[0];
    setWl({ key, operation: m.operations[0].value, value: '', values: [] });
  };
  const addWhitelist = () => {
    const isList = wlMetric.valueKind !== 'text';
    if (isList ? wl.values.length === 0 : !wl.value.trim()) return;
    const entry: BotRequestWhitelistRule = {
      key: wl.key,
      operation: wl.operation,
      get_only: false,
      value: isList ? wl.values : wl.value.trim(),
    };
    set('request_whitelist', [...form.request_whitelist, entry]);
    setWl((w) => ({ ...w, value: '', values: [] }));
  };
  const removeWhitelist = (i: number) =>
    set(
      'request_whitelist',
      form.request_whitelist.filter((_, idx) => idx !== i),
    );

  // Crawl Control selection.
  const crawlerSet = useMemo(() => new Set(form.crawl_control), [form.crawl_control]);
  const toggleCrawler = (cid: number) =>
    set(
      'crawl_control',
      crawlerSet.has(cid) ? form.crawl_control.filter((x) => x !== cid) : [...form.crawl_control, cid],
    );

  // Apply-to selection.
  const siteSet = useMemo(() => new Set(form.sites), [form.sites]);
  const toggleSite = (sid: number) =>
    set('sites', siteSet.has(sid) ? form.sites.filter((x) => x !== sid) : [...form.sites, sid]);

  const create = useCreateServiceBotStrategy(serviceId ?? '', {
    onSuccess: () => {
      // track feature engagement
      trackFeatureAction('waap', 'bot_strategy', 'create');
      show({ tone: 'success', title: 'Bot management rule created' });
      navigate(`${base}/bot-management`);
    },
    onError: (e) => show({ tone: 'danger', title: 'Create failed', description: errMessage(e) }),
  });
  const update = useUpdateServiceBotStrategy(serviceId ?? '', id ?? '', {
    onSuccess: () => {
      // track feature engagement
      trackFeatureAction('waap', 'bot_strategy', 'edit');
      show({ tone: 'success', title: 'Bot management rule saved' });
      navigate(`${base}/bot-management`);
    },
    onError: (e) => show({ tone: 'danger', title: 'Save failed', description: errMessage(e) }),
  });
  const saving = create.isPending || update.isPending;

  const nameValid = form.name.trim().length > 0 && form.name.trim().length <= NAME_MAX;
  const canSubmit = nameValid && !saving;

  const submit = () => {
    const payload: BotStrategy = { ...form, name: form.name.trim(), description: form.description.trim() };
    if (isEdit) update.mutate(payload);
    else create.mutate(payload);
  };

  if (isEdit && loadingExisting) {
    return (
      <Container size="xl">
        <Skeleton className={styles.skeletonBlock} />
      </Container>
    );
  }

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={
          <WaapConsoleBreadcrumbs
            trail={[
              { label: 'Bot Management', slug: 'bot-management' },
              { label: isEdit ? 'Edit Rule' : 'Bot Management Rule' },
            ]}
          />
        }
        title="Bot Management Rule"
      />

      <div style={{ display: 'grid', gap: 'var(--vn-space-5)' }}>
        {/* General */}
        <Card flat>
          <CardHeader>
            <CardTitle>General</CardTitle>
          </CardHeader>
          <CardBody>
            <div className={styles.fieldGrid}>
              <Field
                label="Name"
                required
                description={`1-${NAME_MAX} characters. Valid: 0-9, A-Z, a-z, space, . _ - ( ) * +`}
              >
                {(props) => (
                  <Input
                    {...props}
                    value={form.name}
                    maxLength={NAME_MAX}
                    placeholder="Rule name"
                    onChange={(e) => set('name', e.target.value)}
                  />
                )}
              </Field>
              <Field label="Description" description={`Up to ${DESC_MAX} characters.`}>
                {(props) => (
                  <div>
                    <Textarea
                      {...props}
                      value={form.description}
                      maxLength={DESC_MAX}
                      placeholder="Description"
                      onChange={(e) => set('description', e.target.value)}
                    />
                    <div className={styles.charCount}>
                      {form.description.length}/{DESC_MAX}
                    </div>
                  </div>
                )}
              </Field>
            </div>
          </CardBody>
        </Card>

        {/* Dynamic Token Protection — always on */}
        <Card flat>
          <CardHeader>
            <CardTitle>Dynamic Token Protection</CardTitle>
            <CardDescription>Dynamically embeds an encrypted security token into web application requests to identify and protect against automated threats. Always enabled.</CardDescription>
          </CardHeader>
          <CardBody>
            <label className={styles.enableRow}>
              <Checkbox checked disabled />
              <span>Enable</span>
            </label>
          </CardBody>
        </Card>

        {/* Detection behaviours */}
        <Card flat>
          <CardHeader>
            <CardTitle>Detection</CardTitle>
          </CardHeader>
          <CardBody>
            <div className={styles.fieldGrid}>
              <CheckboxField
                checked={form.silence_challenge_enabled}
                onChange={(v) => set('silence_challenge_enabled', v)}
                label="Silence Challenge"
                description="Passively collect user and device signals to determine human vs bot — without any visible challenge."
              />
              {form.silence_challenge_enabled && (
                <Field
                  label="Silence Challenge Action"
                  required
                  description="How requests flagged as bot threats are handled."
                >
                  {() => (
                    <Select
                      value={form.silence_challenge_action}
                      onValueChange={(v) => set('silence_challenge_action', v)}
                    >
                      <SelectTrigger className={styles.selectWide}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {BOT_SILENCE_ACTIONS.map((o) => (
                          <SelectItem key={o.value} value={o.value}>
                            {o.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </Field>
              )}

              <CheckboxField
                checked={form.crawl_control_enabled}
                onChange={(v) => set('crawl_control_enabled', v)}
                label="Crawl Control"
                description="When enabled, known crawlers will be identified and blocked from scraping the website."
              />
              {form.crawl_control_enabled && (
                <div className={styles.stack2}>
                  <div className={styles.row2}>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => set('crawl_control', ALL_CRAWLER_IDS)}
                    >
                      Select all
                    </Button>
                    <Button variant="secondary" size="sm" onClick={() => set('crawl_control', [])}>
                      Clear all
                    </Button>
                    <span className={styles.mutedSm}>
                      {form.crawl_control.length}/{BOT_CRAWLERS.length} selected
                    </span>
                  </div>
                  <div className={styles.crawlerGrid}>
                    {BOT_CRAWLERS.map((c) => (
                      <label key={c.id} className={styles.checkLabel}>
                        <Checkbox
                          checked={crawlerSet.has(c.id)}
                          onCheckedChange={() => toggleCrawler(c.id)}
                        />
                        <span>{c.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <CheckboxField
                checked={form.ajax_enabled}
                onChange={(v) => set('ajax_enabled', v)}
                label="AJAX Requests"
                description="When enabled, AJAX requests will be embedded with an encrypted security token."
              />
              <CheckboxField
                checked={form.anti_debugging_enabled}
                onChange={(v) => set('anti_debugging_enabled', v)}
                label="Anti-Debugging"
                description="When enabled, anti-debugging monitors and prevents running debugging tools from making hacking attempts."
              />
            </div>
          </CardBody>
        </Card>

        {/* Browser Interrogation */}
        <Card flat>
          <CardHeader>
            <CardTitle>Browser Interrogation</CardTitle>
            <CardDescription>Analyse browser behaviour and device signals; requests matching a condition are blocked.</CardDescription>
          </CardHeader>
          <CardBody>
            <div className={styles.conditionRow}>
              <Field label="Metrics" required description="Browser or device signal to evaluate (mouse, keyboard, touch, extension, or system language).">
                {() => (
                  <Select value={bi.matchParameter} onValueChange={(v) => setBi((s) => ({ ...s, matchParameter: v }))}>
                    <SelectTrigger className={styles.select220}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {BOT_METRICS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>
              <Field label="Matching Condition" required description="How the signal is compared to the value.">
                {() => (
                  <Select value={bi.matchOperator} onValueChange={(v) => setBi((s) => ({ ...s, matchOperator: v }))}>
                    <SelectTrigger className={styles.select160}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {BOT_METRIC_OPERATORS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>
              <Field label="Value" required description="Expected signal state — a request matches when the signal equals this (e.g. no mouse activity ⇒ likely automated).">
                {() => (
                  <Select value={bi.matchValue} onValueChange={(v) => setBi((s) => ({ ...s, matchValue: v }))}>
                    <SelectTrigger className={styles.select120}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {BOT_METRIC_VALUES.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>
              <Button variant="secondary" startIcon={<Plus size={14} />} onClick={addInterrogation}>
                Add
              </Button>
            </div>

            <ConditionTable
              rows={form.browser_interrogation_list.map((r) => [
                labelOf(BOT_METRICS, r.matchParameter),
                labelOf(BOT_METRIC_OPERATORS, r.matchOperator),
                labelOf(BOT_METRIC_VALUES, r.matchValue),
              ])}
              onRemove={removeInterrogation}
            />
          </CardBody>
        </Card>

        {/* Request Whitelist */}
        <Card flat>
          <CardHeader>
            <CardTitle>Request Whitelist</CardTitle>
            <CardDescription>Requests matching these conditions bypass bot management detection.</CardDescription>
          </CardHeader>
          <CardBody>
            <div className={styles.conditionRow}>
              <Field label="Metrics" required description="Request attribute to match — path, file extension, user agent, or country.">
                {() => (
                  <Select value={wl.key} onValueChange={changeWlMetric}>
                    <SelectTrigger className={styles.select160}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {BOT_WHITELIST_METRICS.map((m) => (
                        <SelectItem key={m.key} value={m.key}>
                          {m.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>
              <Field label="Matching Condition" required description="How the attribute is compared to the value.">
                {() => (
                  <Select value={wl.operation} onValueChange={(v) => setWl((s) => ({ ...s, operation: v }))}>
                    <SelectTrigger className={styles.select160}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {wlMetric.operations.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>
              <Field label="Value" required description="Value(s) to match; requests satisfying this condition bypass bot detection.">
                {(props) =>
                  wlMetric.valueKind === 'text' ? (
                    <Input
                      {...props}
                      value={wl.value}
                      placeholder={wl.key === 'ua' ? 'Mozilla/5.0…' : '/api/v3'}
                      className={styles.input260}
                      onChange={(e) => setWl((s) => ({ ...s, value: e.target.value }))}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addWhitelist();
                        }
                      }}
                    />
                  ) : (
                    <div className={styles.input260}>
                      <MultiSelect
                        creatable
                        options={wlMetric.valueKind === 'country' ? countryOptions : []}
                        value={wl.values}
                        placeholder={wlMetric.valueKind === 'country' ? 'Select countries…' : 'Add extensions…'}
                        onChange={(vals) => setWl((s) => ({ ...s, values: vals }))}
                      />
                    </div>
                  )
                }
              </Field>
              <Button variant="secondary" startIcon={<Plus size={14} />} onClick={addWhitelist}>
                Add
              </Button>
            </div>

            <ConditionTable
              rows={form.request_whitelist.map((r) => [
                botWhitelistMetricLabel(r.key),
                botWhitelistOpLabel(r.key, r.operation),
                Array.isArray(r.value) ? r.value.join(', ') : r.value,
              ])}
              onRemove={removeWhitelist}
            />
          </CardBody>
        </Card>

        {/* Apply to */}
        <Card flat>
          <CardHeader>
            <CardTitle>Apply to</CardTitle>
            <CardDescription>Select the websites this rule protects.</CardDescription>
          </CardHeader>
          <CardBody>
            {sites.length === 0 ? (
              <EmptyState icon={<Globe size={20} />} title="No websites available" description="Enable WAAP protection on a website first, then apply this rule." />
            ) : (
              <div className={styles.stack2}>
                {sites.map((s) => (
                  <label key={s.id} className={styles.checkLabelPlain}>
                    <Checkbox checked={siteSet.has(s.id)} onCheckedChange={() => toggleSite(s.id)} />
                    <span>{s.domain}</span>
                  </label>
                ))}
              </div>
            )}
          </CardBody>
        </Card>

        <div className={styles.formActions}>
          <Button variant="secondary" onClick={() => navigate(`${base}/bot-management`)}>
            Cancel
          </Button>
          <Button disabled={!canSubmit} onClick={submit}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </Container>
  );
}

/** A compact read-only table of added condition rows (Metrics / Condition / Value
 *  + remove), shared by Browser Interrogation and Request Whitelist. */
function ConditionTable({
  rows,
  onRemove,
}: {
  rows: string[][];
  onRemove: (i: number) => void;
}) {
  if (rows.length === 0) return null;
  return (
    <div className={styles.tableWrap}>
      <div className={styles.tableHead}>
        <div className={styles.cell}>Metrics</div>
        <div className={styles.cell}>Matching Condition</div>
        <div className={styles.cell}>Value</div>
        <div className={styles.cell}>Operation</div>
      </div>
      {rows.map((r, i) => (
        <div key={i} className={styles.tableRow}>
          <div className={styles.cell}>{r[0]}</div>
          <div className={styles.cell}>{r[1]}</div>
          <div className={styles.cell}>{r[2]}</div>
          <div className={styles.cell}>
            <IconButton aria-label="Remove" variant="ghost" onClick={() => onRemove(i)}>
              <Trash2 size={16} />
            </IconButton>
          </div>
        </div>
      ))}
    </div>
  );
}
